import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Zentraler Modal-Stack.
 *
 * Dialoge registrieren sich beim Öffnen über `useModalStackLayer` und erhalten
 * ihren z-Index dynamisch aus der Stack-Position: zuletzt geöffnet = oben.
 * Damit ist die Stapelreihenfolge unabhängig davon, über welchen Weg ein
 * Dialog geöffnet wurde – statische z-Index-Klassen (`zIndexClass`) dienen nur
 * noch als Fallback außerhalb des Providers.
 *
 * Fenster-Verbünde (Dokumenteneditor, Projektübersicht, Stundennachweis-Flow)
 * teilen sich eine `group`: Alle Dialoge einer Gruppe bleiben zusammenhängend
 * gestapelt und werden gemeinsam nach vorne geholt (`bringGroupToFront`),
 * z. B. wenn der Editor erneut geöffnet wird, während die Projektübersicht
 * darüber liegt. Verschachtelte Dialoge erben die Gruppe automatisch über
 * `ModalStackGroup` (React-Context wirkt auch über Portale hinweg).
 */

/**
 * Basis über allen statischen Ebenen (Seiten-Chrome ≤ z-40, Alt-Leiter ≤ z-380).
 * Portalierte Helfer, die über jedem Dialog liegen müssen (Kontextmenüs,
 * Autocomplete-Listboxen), verwenden z-[900]+; Toasts z-[2000].
 */
export const MODAL_STACK_BASE_Z = 500;
export const MODAL_STACK_STEP_Z = 10;

/** Fenster-Verbünde, die gemeinsam gestapelt/nach vorne geholt werden. */
export const MODAL_STACK_GROUP_DOCUMENT_EDITOR = "documentEditor";
export const MODAL_STACK_GROUP_PROJECT_OVERVIEW = "projectOverview";
export const MODAL_STACK_GROUP_TIMESHEET = "timesheet";

interface StackEntry {
  id: string;
  group: string | null;
}

interface ModalStackContextValue {
  entries: StackEntry[];
  register: (id: string, group: string | null) => void;
  unregister: (id: string) => void;
  bringToFront: (id: string) => void;
  bringGroupToFront: (group: string) => void;
}

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

const ModalStackGroupContext = createContext<string | null>(null);

/**
 * Weist allen Dialogen im Teilbaum (auch über Portale) dieselbe Stack-Gruppe
 * zu, sofern sie keine eigene Gruppe angeben.
 */
export function ModalStackGroup({ group, children }: { group: string; children: ReactNode }) {
  return <ModalStackGroupContext.Provider value={group}>{children}</ModalStackGroupContext.Provider>;
}

/** Gruppe ans Ende (= nach vorne), innere Reihenfolge bleibt erhalten. */
function moveGroupToEnd(entries: StackEntry[], group: string): StackEntry[] {
  const inGroup = entries.filter((e) => e.group === group);
  if (inGroup.length === 0) return entries;
  const alreadyAtEnd = entries
    .slice(entries.length - inGroup.length)
    .every((e) => e.group === group);
  if (alreadyAtEnd) return entries;
  return [...entries.filter((e) => e.group !== group), ...inGroup];
}

export function ModalStackProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<StackEntry[]>([]);

  // Für die Desktop-App: Der Electron-Main-Prozess prüft darüber, ob ein
  // Fenster „belegt“ ist (offener Dialog/Editor), bevor er es für die
  // Zweitfenster-Navigation übersteuert (siehe `openElectronInOtherWindow`).
  useEffect(() => {
    window.__ehOpenModalCount = entries.length;
    return () => {
      window.__ehOpenModalCount = 0;
    };
  }, [entries.length]);

  const register = useCallback((id: string, group: string | null) => {
    setEntries((prev) => {
      const withoutId = prev.filter((e) => e.id !== id);
      // Neuer Dialog einer Gruppe holt den ganzen Verbund nach vorne und
      // legt sich obenauf (entspricht dem bisherigen focusSurface-Verhalten).
      const base = group ? moveGroupToEnd(withoutId, group) : withoutId;
      return [...base, { id, group }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((prev) =>
      prev.some((e) => e.id === id) ? prev.filter((e) => e.id !== id) : prev,
    );
  }, []);

  const bringToFront = useCallback((id: string) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (!entry) return prev;
      if (entry.group) return moveGroupToEnd(prev, entry.group);
      if (prev[prev.length - 1]?.id === id) return prev;
      return [...prev.filter((e) => e.id !== id), entry];
    });
  }, []);

  const bringGroupToFront = useCallback((group: string) => {
    setEntries((prev) => moveGroupToEnd(prev, group));
  }, []);

  const value = useMemo(
    () => ({ entries, register, unregister, bringToFront, bringGroupToFront }),
    [entries, register, unregister, bringToFront, bringGroupToFront],
  );

  return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
}

export interface ModalStackLayer {
  /**
   * Dynamischer z-Index für das Dialog-Root-Element (per `style`, überschreibt
   * die Tailwind-Klasse). `undefined` außerhalb des Providers – dann greift
   * die statische `zIndexClass` als Fallback.
   */
  zIndex: number | undefined;
  /** Holt den Dialog (bzw. seinen Fenster-Verbund) nach vorne. */
  bringToFront: () => void;
}

/**
 * Meldet einen Dialog am zentralen Modal-Stack an, solange `open` true ist.
 * `group` überschreibt die per `ModalStackGroup` geerbte Gruppe.
 */
export function useModalStackLayer(open: boolean, group?: string): ModalStackLayer {
  const id = useId();
  const ctx = useContext(ModalStackContext);
  const inheritedGroup = useContext(ModalStackGroupContext);
  const effectiveGroup = group ?? inheritedGroup;
  const lastZIndexRef = useRef<number | undefined>(undefined);

  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useLayoutEffect(() => {
    if (!open || !register || !unregister) return;
    register(id, effectiveGroup);
    return () => unregister(id);
  }, [open, id, effectiveGroup, register, unregister]);

  const index = ctx ? ctx.entries.findIndex((e) => e.id === id) : -1;
  const zIndex = index >= 0 ? MODAL_STACK_BASE_Z + index * MODAL_STACK_STEP_Z : undefined;
  if (zIndex != null) {
    lastZIndexRef.current = zIndex;
  }

  const ctxBringToFront = ctx?.bringToFront;
  const bringToFront = useCallback(() => {
    ctxBringToFront?.(id);
  }, [ctxBringToFront, id]);

  // Während der Schließ-Transition den letzten z-Index behalten, damit der
  // Dialog beim Ausblenden nicht hinter die verbleibenden Dialoge fällt.
  return { zIndex: zIndex ?? lastZIndexRef.current, bringToFront };
}

/** Steuerung für Feature-Provider (z. B. `open()` bei bereits offenem Fenster). */
export function useModalStackControls(): { bringGroupToFront: (group: string) => void } {
  const ctx = useContext(ModalStackContext);
  const bringGroupToFront = ctx?.bringGroupToFront;
  return useMemo(
    () => ({ bringGroupToFront: bringGroupToFront ?? (() => undefined) }),
    [bringGroupToFront],
  );
}
